'use client';

import React from 'react';
import { AdvancedMarker } from '@vis.gl/react-google-maps';
import type { EnrichedRouteStop } from 'src/hooks/routes/types';

interface StopMarkersProps {
  /** Array of route stops to render as markers */
  stops: EnrichedRouteStop[];
  /** Optional callback when a stop marker is clicked */
  onStopClick?: (stop: EnrichedRouteStop) => void;
}

/**
 * StopMarkers renders numbered circular markers for each stop on a route.
 * User location stops are rendered as a pulsing blue dot instead of a number.
 */
const StopMarkers = ({ stops, onStopClick }: StopMarkersProps) => {
  if (!stops || stops.length === 0) {
    return null;
  }

  return (
    <>
      {stops.map((stop) => (
        <AdvancedMarker
          key={stop.id}
          position={{ lat: stop.lat, lng: stop.lng }}
          onClick={() => onStopClick?.(stop)}
          zIndex={100 + stop.position}
        >
          {stop.isUserLocation ? (
            <div
              className="relative flex h-5 w-5 items-center justify-center"
              title="My Location"
              aria-label="Start: My Location"
              data-testid="stop-marker-user-location"
            >
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-500 opacity-50" />
              <span className="relative inline-flex h-3 w-3 rounded-full bg-blue-600 border-2 border-white shadow-md" />
            </div>
          ) : (
            <div
              className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-600 text-white text-sm font-semibold shadow-md border-2 border-white cursor-pointer hover:bg-sky-700 transition-colors"
              title={stop.name}
              role="button"
              aria-label={`Stop ${stop.position + 1}: ${stop.name}`}
              data-testid={`stop-marker-${stop.position}`}
            >
              {stop.position + 1}
            </div>
          )}
        </AdvancedMarker>
      ))}
    </>
  );
};

export default StopMarkers;
